/**
 * Android app-intent funnel (T27.2.11 Part B).
 *
 * When `app.shopista.ca` is opened in a mobile Android *browser* and would show
 * the logged-out login / create-account screen, we redirect to an Android
 * `intent://` URL instead of rendering the auth form. Chrome/Android resolves
 * the intent to the installed app; if it is absent, it follows the
 * `browser_fallback_url` (the Play listing on prod). The point is to route
 * mobile sign-ins INTO the app rather than the mobile browser — the account is
 * identical wherever it is made (same web bundle + backend).
 *
 * This module is navigation-only: it derives the package + fallback from the
 * current origin and builds the intent string. It never touches auth/session
 * logic. Gated at the call site behind `isAndroidBrowser()` (see
 * `lib/platform.ts`) — the `intent://` scheme is Chrome/Android-only.
 */

/** The per-origin app-open target: the Android package + the browser fallback. */
export interface AppIntentTarget {
  /** The Android applicationId to open. */
  packageName: string;
  /** Where Chrome sends the user when the app is not installed. */
  fallbackUrl: string;
}

const PROD_ORIGIN = 'https://app.shopista.ca';
const DEV_ORIGIN = 'https://dev.shopista.ca';

/** Prod applicationId + its public Play listing. */
const PROD_PACKAGE = 'app.shopista.ca';
const PROD_FALLBACK = 'https://play.google.com/store/apps/details?id=app.shopista.ca';

/** Dev applicationId. Dev has no Play listing, so the fallback is best-effort. */
const DEV_PACKAGE = 'app.shopista.ca.dev';

/**
 * Derive the package + fallback for an origin.
 *
 * - Prod origin (`app.shopista.ca`)  → prod package + the Play listing.
 * - Dev origin (`dev.shopista.ca`)   → dev package; no Play listing exists for
 *   dev, so the fallback is best-effort — the dev origin itself, which at least
 *   re-loads the web app rather than dead-ending. The app-open path is what dev
 *   exercises.
 * - Anything else (localhost, a preview host) falls back to the prod target so
 *   the derivation is total; the funnel only fires under `isAndroidBrowser()`
 *   anyway, so an unexpected origin is a benign no-op in practice.
 */
export function resolveAppIntentTarget(origin: string): AppIntentTarget {
  if (origin === DEV_ORIGIN) {
    return { packageName: DEV_PACKAGE, fallbackUrl: `${DEV_ORIGIN}/` };
  }
  if (origin === PROD_ORIGIN) {
    return { packageName: PROD_PACKAGE, fallbackUrl: PROD_FALLBACK };
  }
  return { packageName: PROD_PACKAGE, fallbackUrl: PROD_FALLBACK };
}

/**
 * Build the Android intent URL for an origin.
 *
 * Shape: `intent://<host>/#Intent;scheme=https;package=<pkg>;S.browser_fallback_url=<encoded>;end`.
 * The `browser_fallback_url` is URL-encoded so its own `?`/`&`/`:` characters do
 * not terminate the intent's `;`-separated field list. The host comes from the
 * origin so the app opens on the same origin the browser was on.
 *
 * Pure + origin-parameterized so it is unit-testable and never reads a global.
 */
export function buildAppIntentUrl(origin: string): string {
  const { packageName, fallbackUrl } = resolveAppIntentTarget(origin);
  const host = new URL(origin).host;
  const encodedFallback = encodeURIComponent(fallbackUrl);
  return `intent://${host}/#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodedFallback};end`;
}

/**
 * One-shot guard: the intent navigation may fire at most once per page load.
 * The call site runs in a React render body, which can re-run (StrictMode
 * double-invokes render in dev; any re-render before the browser navigates
 * away re-runs it). Without this, each run would re-assign `window.location.href`
 * and Android would launch the intent twice — the user sees two "open Play?"
 * prompts.
 */
let redirected = false;

/**
 * Fire the funnel: replace the current document location with the intent URL for
 * the current origin. Side-effecting — call only under `isAndroidBrowser()`, at
 * the auth entry, after the guest guard. Reads `window.location.origin`.
 *
 * Navigates at most once per page load (module-level `redirected` guard).
 */
export function redirectToApp(): void {
  if (redirected) return;
  redirected = true;
  window.location.href = buildAppIntentUrl(window.location.origin);
}
